import React, { useState } from 'react';
import { Trans } from 'react-i18next';
import { useHistory } from 'react-router';
import { track } from '@amplitude/analytics-browser';
import {
  Alert,
  Box,
  Card,
  Checkbox,
  Icon,
  Link,
  Radio,
  Text,
  Title,
} from '@nimbus-ds/components';
import { IdCardIcon } from '@nimbus-ds/icons';
import AppLayout from 'App/components/AppLayout';
import { ROUTES } from 'App/Routes/constants';
import { FormattedMultilineText } from 'commons/components';
import { ID_VALIDATION_ROUTES } from 'commons/constants/idValidationRoutes';
import { useBankAccount } from 'commons/hooks';
import {
  formatCuit,
  getLegalEntityType,
  useTranslationWithPrefix,
} from 'commons/utils';
import FooterButtons from '../FooterButtons';
import { useIdValidationRoutes } from '../hooks';

enum RepresentationType {
  Statute = 'statute',
  PowerOfAttorney = 'powerOfAttorney',
  NoAuthorization = 'noAuthorization',
}

const REPRESENTATION_SELECTED: Record<RepresentationType, string> = {
  [RepresentationType.Statute]: 'social_contract',
  [RepresentationType.PowerOfAttorney]: 'power',
  [RepresentationType.NoAuthorization]: 'no_authorization',
};

function Representation(): JSX.Element {
  const { t } = useTranslationWithPrefix('identityValidation.representation');
  const history = useHistory();
  const { goToNextRoute } = useIdValidationRoutes();
  const bankAccount = useBankAccount();

  const [selectedOption, setSelectedOption] = useState<
    RepresentationType | undefined
  >();
  const [isDeclarationChecked, setIsDeclarationChecked] = useState(true);

  const legalEntityType = getLegalEntityType(bankAccount.data?.fiscalDocument);

  const hasAuthorization =
    selectedOption === RepresentationType.Statute ||
    selectedOption === RepresentationType.PowerOfAttorney;

  const isNoAuthorization =
    selectedOption === RepresentationType.NoAuthorization;

  const canContinue = hasAuthorization;

  const handleSelectOption = (option: RepresentationType) => {
    setSelectedOption(option);
    track('pago-nube-prueba-de-vida-representation-select', {
      representation_selected: REPRESENTATION_SELECTED[option],
      legal_entity_type: legalEntityType,
    });
  };

  const handleSubmit = () => {
    track('pago-nube-prueba-de-vida-representation-continue-click', {
      has_accepted_declaration: isDeclarationChecked,
      representation_selected: selectedOption
        ? REPRESENTATION_SELECTED[selectedOption]
        : undefined,
      legal_entity_type: legalEntityType,
    });
    if (selectedOption && hasAuthorization) {
      goToNextRoute({
        representationType: selectedOption,
        isPepOrSo: !isDeclarationChecked,
      });
    }
  };

  const handleBack = () => {
    track('pago-nube-prueba-de-vida-representation-back_click', {
      has_selected_option: !!selectedOption,
      representation_selected: selectedOption
        ? REPRESENTATION_SELECTED[selectedOption]
        : undefined,
      legal_entity_type: legalEntityType,
    });
    history.goBack();
  };

  const handleDeclarationExplanationClick = () => {
    track('pago-nube-prueba-de-vida-declaration-explanation-click', {
      legal_entity_type: legalEntityType,
    });
    history.push(
      `${ROUTES.IDENTITY_VALIDATION}${ID_VALIDATION_ROUTES.PEP_OR_SO_INFO}`,
    );
  };

  return (
    <AppLayout
      title={t('title')}
      footer={
        <FooterButtons
          onCancelClick={handleBack}
          onSubmitClick={handleSubmit}
          disabled={!canContinue}
        />
      }
    >
      <Box display="flex" flexDirection="column" gap="4">
        <Text>{t('description')}</Text>

        {bankAccount.data?.fiscalDocument && (
          <Box display="flex" flexDirection="row" gap="2">
            <Icon source={<IdCardIcon />} color="primary-interactive" />
            <FormattedMultilineText formattedFontWeight="medium">
              {t('cuitOrCuil', {
                document: formatCuit(bankAccount.data.fiscalDocument),
              })}
            </FormattedMultilineText>
          </Box>
        )}

        <Card>
          <Card.Header>
            <Title as="h4">{t('cardTitle')}</Title>
          </Card.Header>
          <Box display="flex" flexDirection="column" gap="4">
            <Text color="neutral-textLow">{t('cardSubtitle')}</Text>

            <Box display="flex" flexDirection="column" gap="3">
              <Radio
                id="representation-statute"
                label={t('options.statute')}
                name="representationOptions"
                checked={selectedOption === RepresentationType.Statute}
                onChange={() => handleSelectOption(RepresentationType.Statute)}
              />
              <Radio
                id="representation-power-of-attorney"
                label={t('options.powerOfAttorney')}
                name="representationOptions"
                checked={selectedOption === RepresentationType.PowerOfAttorney}
                onChange={() =>
                  handleSelectOption(RepresentationType.PowerOfAttorney)
                }
              />
              <Radio
                id="representation-no-authorization"
                label={t('options.noAuthorization')}
                name="representationOptions"
                checked={selectedOption === RepresentationType.NoAuthorization}
                onChange={() =>
                  handleSelectOption(RepresentationType.NoAuthorization)
                }
              />
            </Box>
          </Box>
        </Card>

        {hasAuthorization && (
          <Box display="flex" flexDirection="row" alignItems="center" gap="2">
            <Checkbox
              aria-label={t('declarationCheckboxAriaLabel')}
              name="declaration-checkbox"
              checked={isDeclarationChecked}
              onChange={(event) =>
                setIsDeclarationChecked(event.target.checked)
              }
            />
            <Text color="neutral-textLow">
              <Trans
                t={t}
                i18nKey="declaration"
                components={{
                  PepLink: (
                    <Link
                      as="button"
                      appearance="primary"
                      onClick={handleDeclarationExplanationClick}
                    >
                      {''}
                    </Link>
                  ),
                }}
              />
            </Text>
          </Box>
        )}

        {isNoAuthorization && (
          <Alert
            show
            appearance="warning"
            title={t('noAuthorizationAlert.title')}
          >
            {t('noAuthorizationAlert.description')}
          </Alert>
        )}
      </Box>
    </AppLayout>
  );
}

export default Representation;
